import {
  WSAutocomplete,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSFlexGrid,
  WSForm,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import {
  useMemberClientQuery,
  useMemberClientsQuery
} from "../../../query/payments/queries";
import { getClients } from "../../../query/payments/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { AdditionalEmailsField } from "../AdditionalEmailsField/AdditionalEmailsField";
import { NewClientIntegrations } from "../NewClientIntegrations/NewClientIntegrations";
import { QuickbooksWrapper } from "../QuickbooksWrapper/QuickbooksWrapper";
import { useInvoicesFormContext } from "./InvoicesForm";

type EmailCC = {
  email: string;
};

export type ClientSectionValues = {
  client: {
    memberClientId: string;
    company: string;
    contact: string;
    contactEmail: string;
    contactEmailsCC: EmailCC[];
    qboCustomerId?: string;
    qboItemId?: string;
  };
};

type Props = {} & WSElementProps;

export const ClientSection: React.FC<Props> = ({ ...elementProps }) => {
  const memberClientsQuery = useMemberClientsQuery();
  const memberClients = getClients(memberClientsQuery.data || []);
  const { invoice, invoiceTemplate } = useInvoicesFormContext();

  const {
    setValue,
    register,
    watch,
    errors,
    trigger,
    formState
  } = useFormContext();

  useEffect(() => {
    register("client.memberClientId");
    register("client.company");
    register("client.contact");
  }, [register]);

  const company = watch("client.company");
  const contact = watch("client.contact");

  const companyValues = new Set<string>();
  memberClients
    .filter(memberClient => memberClient.company)
    .forEach(memberClient => {
      companyValues.add(memberClient.company || "");
    });

  const nameValues = new Set<string>();
  memberClients
    .filter(memberClient => (memberClient.company || "") === (company || ""))
    .forEach(memberClient => {
      nameValues.add(memberClient.name || "");
    });

  const revalidate = useCallback(() => {
    if (formState.isSubmitted) {
      trigger(["client.contact", "client.company", "client.contactEmail"]);
    }
  }, [formState, trigger]);

  return (
    <WSElement {...elementProps}>
      <WSFlexGrid container spacing="S">
        <WSFlexGrid item desktop="7">
          {invoice || invoiceTemplate ? (
            <ExistingClientForm
              memberClientId={
                (invoice?.memberClientId as string | undefined) ||
                (invoiceTemplate?.invoiceData.memberClientId as
                  | string
                  | undefined) ||
                ""
              }
              editMemberClientPath={
                invoice
                  ? `/member/invoices/${invoice.invoiceId}/edit/clients/${invoice.memberClientId}`
                  : invoiceTemplate
                  ? `/member/invoices/template/${invoiceTemplate.invoiceTemplateId}/edit/clients/${invoiceTemplate.invoiceData.memberClientId}`
                  : ""
              }
            />
          ) : (
            <>
              <WSText.Heading3 mb="S">Client</WSText.Heading3>

              <WSForm.Field name="client.company" mb="M">
                <WSForm.Label>Client company (optional)</WSForm.Label>
                <WSAutocomplete
                  name="client.company"
                  value={company}
                  onChange={newValue => {
                    setValue("client.company", newValue);
                    revalidate();
                  }}
                  options={[...companyValues].map(value => ({
                    value
                  }))}
                  onBlur={revalidate}
                  onAutocomplete={(newCompany: string) => {
                    if (contact) {
                      const matchingMemberClient = memberClients.find(
                        memberClient =>
                          memberClient.company === newCompany &&
                          memberClient.name === contact
                      );

                      if (matchingMemberClient) {
                        setValue(
                          "client.memberClientId",
                          matchingMemberClient.memberClientId
                        );
                        setValue(
                          "client.contactEmail",
                          matchingMemberClient.emailTo
                        );
                        return;
                      }
                    }

                    const matchingMemberClients = memberClients.filter(
                      memberClient => memberClient.company === company
                    );

                    if (matchingMemberClients.length === 1) {
                      setValue("client.contact", matchingMemberClients[0].name);
                    } else {
                      setValue("client.memberClientId", "");
                      setValue("client.contact", "");
                      setValue("client.contactEmail", "");
                    }

                    revalidate();
                  }}
                  error={errors?.client?.company?.message}
                />
                <WSForm.Error name="client.company" />
              </WSForm.Field>

              <WSForm.Field name="client.contact" mb="M">
                <WSForm.Label>Contact name</WSForm.Label>
                <WSAutocomplete
                  name="client.contact"
                  value={contact}
                  onChange={newValue => {
                    setValue("client.contact", newValue);
                    revalidate();
                  }}
                  options={[...nameValues].map(value => ({
                    value
                  }))}
                  onBlur={revalidate}
                  onAutocomplete={newContact => {
                    const matchingMemberClient = memberClients.find(
                      memberClient =>
                        memberClient.company === company &&
                        memberClient.name === newContact
                    );

                    if (matchingMemberClient) {
                      setValue(
                        "client.memberClientId",
                        matchingMemberClient.memberClientId
                      );
                      setValue(
                        "client.contactEmail",
                        matchingMemberClient.emailTo
                      );
                    }

                    revalidate();
                  }}
                  error={errors?.client?.contact?.message}
                />
                <WSForm.Error name="client.contact" />
              </WSForm.Field>

              <WSForm.Field
                mb="M"
                name="client.contactEmail"
                label="Contact email"
                component={WSTextInput}
              />

              <AdditionalEmailsField
                name="client.contactEmailsCC"
                inputLabel="Contact email CC"
                buttonLabel="Add email recipient"
                buttonDescription="Additional email addresses will be cc’d"
              />

              <QuickbooksWrapper>
                <WSForm.Values
                  names={[
                    "client.contactEmail",
                    "client.company",
                    "client.contact"
                  ]}
                >
                  {data => {
                    const matchingMemberClient = memberClients.find(
                      memberClient =>
                        (data["client.company"] || "") ===
                          (memberClient.company || "") &&
                        (data["client.contact"] || "") ===
                          (memberClient.name || "") &&
                        data["client.contactEmail"] === memberClient.emailTo
                    );

                    return !matchingMemberClient ? (
                      <NewClientIntegrations />
                    ) : null;
                  }}
                </WSForm.Values>
              </QuickbooksWrapper>
            </>
          )}
        </WSFlexGrid>
      </WSFlexGrid>
    </WSElement>
  );
};

const ExistingClientForm: React.FC<{
  memberClientId: string;
  editMemberClientPath: string;
}> = ({ memberClientId, editMemberClientPath }) => {
  const memberClientQuery = useMemberClientQuery(memberClientId);
  const history = useHistory();
  const location = useLocation();

  const { register } = useFormContext();

  useEffect(() => {
    register("client.contactEmail");
  }, [register]);

  return (
    <WSQueries queries={{ memberClientQuery }}>
      {({ memberClientQuery: { data: memberClient } }) => (
        <>
          <WSFlexBox.CenterY mb="S" wrap="nowrap" justify="space-between">
            <WSText.Heading3>Client</WSText.Heading3>

            <WSButton.Link
              onClick={() => {
                history.push(editMemberClientPath, {
                  backPath: location.pathname
                });
              }}
              type="button"
            >
              Edit
            </WSButton.Link>
          </WSFlexBox.CenterY>

          <WSForm.Field name="client.company" mb="M">
            <WSForm.Label>Client company</WSForm.Label>
            <WSTextInput
              name="client.company"
              value={memberClient.company}
              disabled
            />
          </WSForm.Field>

          <WSForm.Field name="client.contact" mb="M">
            <WSForm.Label>Contact name</WSForm.Label>
            <WSTextInput
              name="client.contact"
              value={memberClient.name}
              disabled
            />
          </WSForm.Field>

          <WSForm.Field name="client.contactEmail" mb="M">
            <WSForm.Label>Contact email</WSForm.Label>
            <WSTextInput
              name="client.contactEmail"
              value={memberClient.emailTo}
              disabled
            />
          </WSForm.Field>

          {memberClient.emailCC &&
            memberClient.emailCC.map((email, index) => (
              <WSForm.Field
                key={email}
                name={`client.contactEmailsCC[${index}]`}
                mb="M"
              >
                <WSForm.Label>Contact email CC {index + 1}</WSForm.Label>
                <WSTextInput
                  name={`client.contactEmailsCC[${index}]`}
                  value={email}
                  disabled
                />
              </WSForm.Field>
            ))}
        </>
      )}
    </WSQueries>
  );
};
